/**
 * DNH — Centralized API client.
 * All backend calls go through here. Uses REACT_APP_BACKEND_URL.
 */

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export async function apiFetch(path, options = {}) {
  const res = await fetch(`${BASE_URL}${path}`, {
    cache: "no-store",
    credentials: "include",
    signal: AbortSignal.timeout(20000),
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) {
    const err = await res.text();
    const error = new Error(`API ${path} → ${res.status}: ${err}`);
    error.status = res.status;
    try { error.data = JSON.parse(err); } catch { error.data = null; }
    throw error;
  }
  return res.json();
}

/** Build query string from params object, skipping null/undefined */
function qs(params = {}) {
  const p = Object.entries(params)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
  return p ? `?${p}` : "";
}

// ─── Today / Home ──────────────────────────────────────────────
export const getToday = (country) =>
  apiFetch(`/api/today${qs({ country })}`);

// ─── 10-Minute Briefs / Trending / Live Map / Archive ─────────
export const getBriefToday = (scope = "global", slug = null) =>
  apiFetch(`/api/brief/today${qs({ scope, slug })}`);

export const getTrending = () =>
  apiFetch("/api/trending");

export const getMapLive = () =>
  apiFetch("/api/map/live");

export const getArchive = (params = {}) =>
  apiFetch(`/api/stories/archive${qs(params)}`);

export const getSourceDetail = (sourceId) =>
  apiFetch(`/api/sources/browse/${encodeURIComponent(sourceId)}`);

export const getSourcesByCountry = (countrySlug) =>
  apiFetch(`/api/sources/by-country/${encodeURIComponent(countrySlug)}`);

export const getBriefShare = (scope, slug, date = null, lens = null) =>
  apiFetch(`/api/brief/share/${scope}/${slug || "global"}${qs({ date, lens })}`);

export const getBriefLenses = () =>
  apiFetch("/api/brief/lenses");

// ─── World Stage ───────────────────────────────────────────────
export const getWorldStage = (country) =>
  apiFetch(`/api/world-stage${qs({ country })}`);

export const getCountries = () =>
  apiFetch("/api/countries");

// ─── Country Intelligence ──────────────────────────────────────
export const getCountry = (slug) =>
  apiFetch(`/api/country/${slug}`);

export const getCountryThinks = (slug) =>
  apiFetch(`/api/country/${slug}/thinks`);

export const getCountryDoes = (slug) =>
  apiFetch(`/api/country/${slug}/does`);

export const getCountryAdvises = (slug) =>
  apiFetch(`/api/country/${slug}/advises`);

export const getCountryDiplomatic = (slug) =>
  apiFetch(`/api/country/${slug}/diplomatic`);

export const synthesizeCountry = (slug) =>
  apiFetch(`/api/country/${slug}/synthesize`, { method: "POST" });

// ─── Category ─────────────────────────────────────────────────
export const getCategory = (slug, country) =>
  apiFetch(`/api/category/${slug}${qs({ country })}`);

export const getCategories = () =>
  apiFetch("/api/categories");

// ─── Topics ───────────────────────────────────────────────────
export const getTopics = (country) =>
  apiFetch(`/api/topics${qs({ country })}`);

export const getTopicDetail = (slug, country) =>
  apiFetch(`/api/topics/${slug}${qs({ country })}`);

// ─── Search ───────────────────────────────────────────────────
export const searchStories = (q, country, filters = {}) => {
  const params = { q, country };
  if (filters.categories?.length) params.categories = filters.categories.join(",");
  if (filters.confidences?.length) params.confidences = filters.confidences.join(",");
  if (filters.date_range) params.date_range = filters.date_range;
  if (filters.topic) params.topic = filters.topic;
  return apiFetch(`/api/search${qs(params)}`);
};

// ─── Ticker ───────────────────────────────────────────────────
const tickerRequests = new Map();
export const getTicker = (country) => {
  const query = qs({ country });
  if (!tickerRequests.has(query)) {
    // Share concurrent/StrictMode reads, but don't keep stale results or errors.
    const request = apiFetch(`/api/signals/ticker${query}`)
      .finally(() => tickerRequests.delete(query));
    tickerRequests.set(query, request);
  }
  return tickerRequests.get(query);
};

// ─── Platform Stats ───────────────────────────────────────────
export const getStats = () =>
  apiFetch("/api/stats");

// ─── Read / Watch / Listen ────────────────────────────────────
export const getRWL = (country) =>
  apiFetch(`/api/rwl${country ? `?country=${country}` : ""}`);

// ─── Ingestion ────────────────────────────────────────────────
export const triggerIngestion = () =>
  apiFetch("/api/ingest", { method: "POST" });

// ─── Admin ────────────────────────────────────────────────────
export const getAdminStats = () => apiFetch("/api/admin/stats");
export const getAdminSources = (params = {}) => apiFetch(`/api/admin/sources${qs(params)}`);
export const createAdminSource = (body) =>
  apiFetch("/api/admin/sources", { method: "POST", body: JSON.stringify(body) });
export const updateAdminSource = (id, updates) =>
  apiFetch(`/api/admin/sources/${id}`, { method: "PATCH", body: JSON.stringify(updates) });
export const deleteAdminSource = (id) =>
  apiFetch(`/api/admin/sources/${id}`, { method: "DELETE" });
export const triggerAdminIngest = () =>
  apiFetch("/api/admin/ingest", { method: "POST" });
